import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Loader2, Filter, X } from 'lucide-react';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useView } from '../../../contexts/ViewContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../hooks/usePortalMode';
import {
  getCounterpartyLabel,
  getCounterpartyFilterPlaceholder,
} from '../../../lib/portalLabels';
import {
  salesOrdersDashboardAPI,
  type SalesOrdersDashboardData,
} from '../../../lib/api/sales-orders-dashboard';
import { coreAPI } from '../../../lib/api';
import SearchableSelect from '../../ui/SearchableSelect';
import { CurrentWeek } from './CurrentWeek';
import { CurrentMonth } from './CurrentMonth';
import { PerWeek } from './PerWeek';
import { YTD } from './YTD';
import {
  SalesOrdersDrillDown,
  type DrillDownScope,
  type DrillDownPeriod,
  type DrillDownExtraFilters,
} from './SalesOrdersDrillDown';

/** Snapshot of the active drill-down. `null` = closed. */
export interface DrillDownState {
  scope: DrillDownScope;
  period: DrillDownPeriod | null;
  title: string;
  subtitle?: string;
  extraFilters?: DrillDownExtraFilters;
}

export type OpenDrillDown = (state: DrillDownState) => void;

type SubTab = 'current_week' | 'current_month' | 'per_week' | 'ytd';

const SUB_TABS: Array<{ id: SubTab; label: string; sub: string }> = [
  { id: 'current_week',  label: 'Current Week',  sub: 'this week' },
  { id: 'current_month', label: 'Current Month', sub: 'this month' },
  { id: 'per_week',      label: 'Per Week',      sub: '12-week trend' },
  { id: 'ytd',           label: 'Year to Date',  sub: 'Jan 1 → today' },
];

interface CustomerOption { id: number; name: string }

/**
 * Shell for the Sales Orders dashboard tab. Owns filter state, fetches the
 * SalesOrdersDashboard payload, and dispatches into the period sub-components.
 *
 * Supplier-side users filter by customer companies on the counterparty FK.
 */
export function SalesOrdersShell() {
  const { selectedCompanyId, selectedSiteId, viewKey } = useView();
  const { formatCurrency, formatDate } = useFormatters();

  // Active supplier scope. Company IS Site in this codebase, so a multi-site
  // helix admin (site-restricted) holds selectedCompanyId=null and switches
  // scope via selectedSiteId. Resolve from whichever is set so the dashboard
  // re-scopes when the admin changes the active Poeton company in the TopBar.
  const scopeSupplierId = selectedCompanyId ?? selectedSiteId;
  const { isFeatureEnabled, isSupplierPortalMode } = useMenuSettings();

  // Customer-side users get ONLY the top KPI summary cards; chart sections and
  // drill-downs are supplier/admin-only.
  const { isCustomer, isSupplier } = usePortalMode();
  const counterpartyLabel = getCounterpartyLabel(isSupplier);
  const counterpartyPlaceholder = getCounterpartyFilterPlaceholder(isSupplier);

  // OTIF visibility is feature-flag gated. When off, OTIF tiles, columns,
  // drill-downs and trend lines are not rendered; OTD stays fully functional.
  const showOtif = isFeatureEnabled('sales_orders_otif');

  const [data, setData] = useState<SalesOrdersDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SubTab>('current_week');
  const [drillDown, setDrillDown] = useState<DrillDownState | null>(null);

  const openDrillDown: OpenDrillDown = (state) => setDrillDown(state);
  const closeDrillDown = () => setDrillDown(null);

  // Filter state — committed values are sent to the API. Part input
  // auto-commits 400ms after the user stops typing.
  const [customerId, setCustomerId] = useState<string>('');
  const [partInput, setPartInput] = useState<string>('');
  const [partCommitted, setPartCommitted] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setPartCommitted(partInput.trim()), 400);
    return () => clearTimeout(t);
  }, [partInput]);

  const [customers, setCustomers] = useState<CustomerOption[]>([]);

  // Counterparty list — same DB rows regardless of supplier-portal label flip.
  // Uses the portal-aware /core/companies/for-filter/ action (scoped per
  // tenant via CustomerSupplierRelationship) instead of the Helix-only
  // /core/companies/ list, which 403s for non-Helix users.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let all: Array<{ id: number; name: string }> = [];
      try {
        // Customer view: the counterparty is the SUPPLIER — list the
        // customer's linked suppliers (Poeton sites), not their own company.
        // Supplier view: list the customers they serve.
        const resp = isCustomer
          ? await coreAPI.getMySuppliers()
          : await coreAPI.getCustomerSitesForFilter();
        if (cancelled) return;
        all = resp.data.results || [];
      } catch { /* dropdown stays empty */ }
      if (cancelled) return;
      setCustomers(
        all
          .map((c) => ({ id: c.id, name: c.name || `${counterpartyLabel} ${c.id}` }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
    })();
    return () => { cancelled = true; };
  }, [isCustomer]);

  // Dashboard fetch — refires when scope or filters change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: Record<string, string | number> = {};
    if (isCustomer) {
      // Customer view: the dropdown picks one of their SUPPLIERS → scope by it.
      if (customerId) params['supplier_id'] = Number(customerId);
    } else {
      if (scopeSupplierId) params['supplier_id'] = Number(scopeSupplierId);
      if (customerId) params['customer_id'] = Number(customerId);
    }
    if (partCommitted) params['part_number'] = partCommitted;

    salesOrdersDashboardAPI
      .get(params)
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // viewKey changes on any scope switch (group/company/site), so the
    // dashboard refetches even when only the active site changes.
  }, [scopeSupplierId, viewKey, customerId, partCommitted]);

  // Filter params shared with the Current-Week stepper refetch — keep in sync
  // with the dashboard fetch effect above.
  const currentWeekFilters = useMemo(() => {
    const p: { supplier_id?: number; customer_id?: number; part_number?: string } = {};
    if (isCustomer) {
      if (customerId) p.supplier_id = Number(customerId);
    } else {
      if (scopeSupplierId) p.supplier_id = Number(scopeSupplierId);
      if (customerId) p.customer_id = Number(customerId);
    }
    if (partCommitted) p.part_number = partCommitted;
    return p;
  }, [isCustomer, scopeSupplierId, customerId, partCommitted]);

  const activeFilters = useMemo(() => {
    const labels: string[] = [];
    if (customerId) {
      const name = customers.find((s) => String(s.id) === customerId)?.name ?? `${counterpartyLabel} ${customerId}`;
      labels.push(`${counterpartyLabel}: ${name}`);
    }
    if (partCommitted) labels.push(`Part: ${partCommitted}`);
    return labels;
  }, [customerId, partCommitted, customers, counterpartyLabel]);

  const clearFilters = () => {
    setCustomerId('');
    setPartInput('');
    setPartCommitted('');
  };

  const filterBar = (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
      role="group"
      aria-label="Dashboard filters"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Filter className="h-3.5 w-3.5" />
          <span>Filters</span>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="so-counterparty" className="text-xs text-slate-500">{counterpartyLabel}</label>
          <div className="w-96">
            <SearchableSelect
              options={customers.map((s) => ({ value: String(s.id), label: s.name }))}
              value={customerId}
              onChange={setCustomerId}
              placeholder={counterpartyPlaceholder}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="so-part" className="text-xs text-slate-500">Part</label>
          <input
            id="so-part"
            type="text"
            value={partInput}
            onChange={(e) => setPartInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPartCommitted(partInput.trim());
              if (e.key === 'Escape') { setPartInput(''); setPartCommitted(''); }
            }}
            placeholder="type part # — applies as you type"
            title="Auto-applies after you stop typing. Esc to clear."
            className="h-8 w-56 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>

        {activeFilters.length > 0 && (
          <button
            onClick={clearFilters}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400"
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </div>

      {activeFilters.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="text-slate-600">Filtered by {activeFilters.join(' · ')}</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {filterBar}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading sales-order KPIs…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card>
          <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {filterBar}

      <div className="border-b border-slate-200" role="tablist" aria-label="Time period">
        <div className="flex gap-1">
          {SUB_TABS.map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 text-sm relative transition-colors ${
                  active ? 'text-slate-900 font-semibold' : 'text-slate-500 font-medium hover:text-slate-800'
                }`}
              >
                <span>{t.label}</span>
                <span className="ml-2 text-[11px] font-normal text-slate-400">{t.sub}</span>
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'current_week'  && <CurrentWeek  data={data} formatCurrency={formatCurrency} formatDate={formatDate} openDrillDown={openDrillDown} showOtif={showOtif} isSupplierPortalMode={isSupplierPortalMode} filterParams={currentWeekFilters} isCustomer={isCustomer} />}
        {activeTab === 'current_month' && <CurrentMonth data={data} formatCurrency={formatCurrency} formatDate={formatDate} openDrillDown={openDrillDown} showOtif={showOtif} isSupplierPortalMode={isSupplierPortalMode} filterParams={currentWeekFilters} isCustomer={isCustomer} />}
        {activeTab === 'per_week'      && <PerWeek      data={data} formatCurrency={formatCurrency} openDrillDown={openDrillDown} showOtif={showOtif} isSupplierPortalMode={isSupplierPortalMode} isCustomer={isCustomer} />}
        {activeTab === 'ytd'           && <YTD          data={data} formatCurrency={formatCurrency} openDrillDown={openDrillDown} showOtif={showOtif} isSupplierPortalMode={isSupplierPortalMode} isCustomer={isCustomer} />}
      </div>

      <SalesOrdersDrillDown
        isOpen={drillDown !== null}
        onClose={closeDrillDown}
        scope={drillDown?.scope ?? null}
        period={drillDown?.period ?? null}
        extraFilters={drillDown?.extraFilters}
        title={drillDown?.title ?? ''}
        subtitle={drillDown?.subtitle}
        customerId={isCustomer ? undefined : (customerId || undefined)}
        supplierId={isCustomer ? (customerId ? Number(customerId) : undefined) : (scopeSupplierId ?? undefined)}
        partNumber={partCommitted || undefined}
        graceDays={data?.otd_grace_days ?? 1}
      />
    </div>
  );
}
